package net.gorillagroove.util

import kotlinx.datetime.*
import kotlinx.datetime.format.char
import kotlinx.datetime.number
import net.gorillagroove.track.Track
import net.gorillagroove.util.SettingType.*
import kotlin.math.*

typealias TrackFormatChangeHandler = () -> Unit

object Formatter {
    var userDateFormat: String
        get() = Settings.getString(DATE_FORMAT, "D/M/YYYY")
        // I could validate this, but the library could be out of date, and I could allow more things to be put in here
        // as time goes on. I am fine with just keeping it stupid. It's pretty low-impact if something goes wrong.
        set(value) = Settings.setString(DATE_FORMAT, value)

    var featuringDelimiterFormat: String
        get() = Settings.getString(TRACK_DISPLAY_FEATURING_DELIMITER, "ft.")
        set(value) {
            Settings.setString(TRACK_DISPLAY_FEATURING_DELIMITER, value)
            emitEvent()
        }

    var trackDisplayFormat: String
        get() = Settings.getString(TRACK_DISPLAY_FORMAT,"{name} - {artist}")
        set(value) {
            Settings.setString(TRACK_DISPLAY_FORMAT, value)
            emitEvent()
        }

    var trackNameDisplayFormat: String
        get() = Settings.getString(TRACK_NAME_DISPLAY_FORMAT, "{name}")
        set(value) {
            Settings.setString(TRACK_NAME_DISPLAY_FORMAT, value)
            emitEvent()
        }

    var trackArtistDisplayFormat: String
        get() = Settings.getString(TRACK_ARTIST_DISPLAY_FORMAT, "{artist} {feat} {featuring}")
        set(value) {
            Settings.setString(TRACK_ARTIST_DISPLAY_FORMAT, value)
            emitEvent()
        }

    internal fun Instant.toUserFormat(timeZone: TimeZone = TimeZone.currentSystemDefault()): String {
        return this.toFormat(userDateFormat, timeZone)
    }

    internal fun Instant.toFormat(format: String, timeZone: TimeZone = TimeZone.currentSystemDefault()): String {
        val date = this.toLocalDateTime(timeZone)
        return format.replace("DD", date.day.zeroPadded())
            .replace("D", date.day.toString())
            .replace("MM", date.month.number.zeroPadded())
            .replace("M", date.month.number.toString())
            .replace("YYYY", date.year.toString())
            .replace("YY", date.year.toString().takeLast(2))
    }

    private fun Int.zeroPadded(): String {
        return if (this >= 10) this.toString() else "0$this"
    }

    fun Long.toReadableByteString(): String {
        if (this < 1000) {
            return "$this B"
        }

        val kb = this / 1000.0
        if (kb < 1000) {
            return "${kb.toSignificantFigures(3)} KB"
        }

        val mb = kb / 1000.0
        if (mb < 1000) {
            return "${mb.toSignificantFigures(3)} MB"
        }

        val gb = mb / 1000.0
        if (gb < 1000) {
            return "${gb.toSignificantFigures(3)} GB"
        }

        val tb = gb / 1000.0
        return "${tb.toSignificantFigures(3)} TB"
    }

    // For iOS that can't easily use extension functions
    fun getReadableByteString(num: Long): String {
        return num.toReadableByteString()
    }

    fun bytesToGbString(bytes: Long): String {
        val gb = (bytes.toDouble() / 1_000_000_000)
        return gb.roundedDecimalString(decimals = 2).trimEnd('0').trimEnd('.')
    }

    internal fun Double.roundedDecimalString(decimals: Int): String {
        val asLong = this.toLong()
        val preDecimalNumbers = if (asLong == 0L) 0 else asLong.toString().length
        return this.toSignificantFigures(preDecimalNumbers + decimals)
    }

    // Would be made easier whenever this is implemented: https://youtrack.jetbrains.com/issue/KT-21644
    // This function is kind of a lie in that it probably isn't the scientific definition of significant figures.
    // But what it will do is the following:
    // 1.123.toSignificantFigures(3) => 1.12
    // 10.123.toSignificantFigures(3) => 10.1
    // 105.823.toSignificantFigures(3) => 106
    internal fun Double.toSignificantFigures(digits: Int): String {
        // My implementation is probably highly naive. But hey, it works on any platform that has MATH.
        val nonDecimalDigits = floor(log10(this)).toInt() + 1

        if (nonDecimalDigits >= digits) {
            return this.roundToLong().toString()
        }

        val nonDecimalComponent = this.toLong()

        // Converts 614.123 into 0.123
        val decimalComponent = this - nonDecimalComponent

        // Now that we have just the decimals, we can shift it over by the number of additional digits we need.
        // Then we can round it, and jam it onto the end of our non-decimal component.
        // So if we want 2 more figures, 0.123 becomes 12.3, then we round it to just 12.
        val decimalsToKeep = digits - nonDecimalDigits
        val decimalAsInt = (decimalComponent * 10.0.pow(decimalsToKeep)).roundToInt()

        return "$nonDecimalComponent.${decimalAsInt.toZeroPaddedString(decimalsToKeep)}"
            .trimEnd('0')
            .trimEnd('.')
    }

    fun getTrackArtistDisplayString(track: Track): String {
        return replaceTrackStrings(track.name, track.artist, track.featuring, artistMode = true)
    }

    private fun getTrackArtistDisplayString(name: String, artist: String, featuring: String): String {
        return replaceTrackStrings(name, artist, featuring, artistMode = true)
    }

    fun getTrackNameDisplayString(track: Track): String {
        return replaceTrackStrings(track.name, track.artist, track.featuring, artistMode = false)
    }

    private fun getTrackNameDisplayString(name: String, artist: String, featuring: String): String {
        return replaceTrackStrings(name, artist, featuring, artistMode = false)
    }

    private fun replaceTrackStrings(name: String, artist: String, featuring: String, artistMode: Boolean): String {
        // If there's no featured artist, then we don't want to display "ft."
        val delimiterToUse = if (featuring.isBlank()) "" else featuringDelimiterFormat

        return (if (artistMode) trackArtistDisplayFormat else trackNameDisplayFormat)
            .replace(if (artistMode) "{artist}" else "{name}", if (artistMode) artist else name)
            .replace("{featuring}", featuring)
            .replace("{feat}", delimiterToUse)
            .trim()
    }

    /**
     * Used by settings menus on the clients to show what their choices will look like
     */
    fun generateExampleDisplayString(): String {
        val track = Track(name = "Set Yourself Free", artist = "Tiësto", featuring = "Krewella")
        return getPlayingTrackDisplayString(track)
    }

    fun getPlayingTrackDisplayString(track: Track): String {
        return getPlayingTrackDisplayString(track.name, track.artist, track.featuring)
    }

    fun getPlayingTrackDisplayString(name: String, artist: String, featuring: String): String {
        return trackDisplayFormat.replace("{name}", getTrackNameDisplayString(name, artist, featuring))
            .replace("{artist}", getTrackArtistDisplayString(name, artist, featuring))
            .trim()
    }

    fun formatToUserDateFormat(instant: Instant): String {
        return instant.toUserFormat()
    }

    /**
     * The provided String must parse to an Instant
     */
    fun formatToUserDateFormat(date: String): String {
        return Instant.parse(date).toUserFormat()
    }

    // There isn't currently a user time format. This will be the same for everybody.
    // However, it's pretty obvious to me that eventually this will be configurable for 12 or 24 hour people
    fun formatToUserTimeFormat(instant: Instant): String {
        val customFormat = LocalDateTime.Format {
            year()
            char('-')
            monthNumber()
            char('-')
            day()
            char(' ')
            amPmHour()
            char(':')
            minute()
            char(':')
            second()
            char(' ')
            amPmMarker("AM", "PM")
        }
        val zone = TimeUtil.timeZoneOverride ?: TimeZone.currentSystemDefault()
        return instant.toLocalDateTime(zone).format(customFormat)
    }

    fun getDurationDisplay(track: Track) = getDurationDisplayFromSeconds(track.length)

    fun getDurationDisplayFromSeconds(seconds: Int): String {
        val hours = seconds / 3600
        val minutes = (seconds / 60) % 60
        val secondsPart = seconds % 60

        return if (hours == 0) {
            "$minutes:${secondsPart.toZeroPaddedString()}"
        } else {
            "$hours:${minutes.toZeroPaddedString()}:${secondsPart.toZeroPaddedString()}"
        }
    }

    private fun Int.toZeroPaddedString(): String {
        return if (this < 10) "0$this" else this.toString()
    }

    private fun Int.toZeroPaddedString(length: Int): String {
        val originalLength = this.toString().length
        val zeroesNeeded = length - originalLength

        return "0".repeat(zeroesNeeded) + this
    }

    internal fun Instant.toTimeAgoString(): String {
        val difference = TimeUtil.now() - this

        fun Long.toPluralizedLabel(label: String): String {
            return "$this $label" + if (this == 1L) "" else "s"
        }

        val timeString = if (difference.inWholeSeconds < 10) {
            return "Just now"
        } else if (difference.inWholeSeconds < 60) {
            difference.inWholeSeconds.toPluralizedLabel("second")
        } else if (difference.inWholeMinutes < 60) {
            difference.inWholeMinutes.toPluralizedLabel("minute")
        } else if (difference.inWholeHours < 24) {
            difference.inWholeHours.toPluralizedLabel("hour")
        } else {
            difference.inWholeDays.toPluralizedLabel("day")
        }

        return "$timeString ago"
    }

    val eventRegistration by lazy { FormatterEventRegistration() }

    @Suppress("RedundantOverride", "unused") // Overrides needed for iOS convenience as it can't handle generic functions
    class FormatterEventRegistration : EventHandlerRegistration<TrackFormatChangeHandler>() {
        fun registerIos(handler: TrackFormatChangeHandler): Int = super.register(handler)
        override fun unregister(handlerId: Int) = super.unregister(handlerId)
    }

    private fun emitEvent() {
        eventRegistration.broadcast { it() }
    }
}

fun Double.round(decimals: Int): String {
    var multiplier = 1.0
    repeat(decimals) { multiplier *= 10 }
    return (kotlin.math.round(this * multiplier) / multiplier).toString()
}
